import { action, computed, observable, makeObservable } from "mobx";
import Agentv2 from "../Agentv2";
import { InteractionType } from "../InteractionType";
import BaseEntity from "../BaseEntity";
// import Evaluation from "../Evaluation";
import { ApplicationUser } from "../Permission/ApplicationUser";

export enum AgentCoachingStatus {
    InProgress,
    Completed,
}

export interface AgentCoachingTarget {
    agentCoachingId: string;
    evaluationId?: string;
    audioMetadataId?: string;
    /**
     * Display data the MyDrafts payload carries for the target, so a draft renders
     * its evaluation number and Edit route without the evaluation dataset the other
     * dashboard tabs load. `evaluationAudioMetadataId` is the conversation behind an
     * evaluation target — deliberately not `audioMetadataId`, which is what makes a
     * target a conversation target.
     */
    evaluationQbId?: number;
    evaluationAudioMetadataId?: string;
    interactionType?: InteractionType;
}

/**
 * A target references either an evaluation or a conversation, never both, so
 * these two helpers partition a coaching's targets.
 */
export function targetedEvaluationIds(coaching: {
    targets?: AgentCoachingTarget[];
}): string[] {
    return (coaching.targets ?? [])
        .map((target) => target.evaluationId)
        .filter((id): id is string => !!id);
}

export function targetedConversationIds(coaching: {
    targets?: AgentCoachingTarget[];
}): string[] {
    return (coaching.targets ?? [])
        .map((target) => target.audioMetadataId)
        .filter((id): id is string => !!id);
}

export class AgentCoaching extends BaseEntity {
    agentId: string;
    agentCoachingId: string;
    agent?: Agentv2;
    /**
     * The evaluations and conversations this coaching covers. Source of truth for
     * everything read from the API — prefer this over the legacy fields below.
     */
    targets?: AgentCoachingTarget[];
    /**
     * @deprecated Legacy write-payload fields. The API still expects them on
     * create/update, so they are derived from `targets` when serializing. Nothing
     * should read them; they go away with the write-path migration.
     */
    evaluationId?: string[];
    /** @deprecated See `evaluationId`. */
    audioMetadataId?: string;
    coachId: string;
    coach?: ApplicationUser;
    @observable improvementNotes?: string;
    @observable agentDoWellNotes?: string;
    @observable focusAreas?: string;
    @observable responseNotes?: string;
    @observable acknowledged?: boolean;
    @observable evalAcknowledged?: boolean;
    @observable coachingStatus: AgentCoachingStatus;
    /**
     * A json value containing an array of strings.
     *
     * @deprecated Replaced by `targets`. Still written by the API, never read.
     */
    @observable targetedEvaluationIds?: string;

    @action
    setImprovementNotes = (notes: string) => {
        this.improvementNotes = notes;
    };

    @action
    setDoWellNotes = (notes: string) => {
        this.agentDoWellNotes = notes;
    };

    @action
    setResponseNotes = (notes: string) => {
        this.responseNotes = notes;
    };

    @action
    setAcknowledged = (arg: boolean) => {
        this.acknowledged = arg;
    };

    @action
    setFocusAreas = (focusAreaQuestions?: { value: string }[]) => {
        if (focusAreaQuestions) {
            this.focusAreas = JSON.stringify(
                focusAreaQuestions.map((value) => value.value),
            );
        } else {
            this.focusAreas = undefined;
        }
    };

    constructor() {
        // TODO: [mobx-undecorate] verify the constructor arguments and the arguments of this automatically generated super call
        super();

        makeObservable(this);
    }

    @computed
    get focusAreaQuestions() {
        const focusAreas = this.focusAreas;

        return focusAreas
            ? (JSON.parse(focusAreas) as string[]).map((value) => ({
                  value: value,
              }))
            : [];
    }
}
